import React, { FC, useEffect, useLayoutEffect, useRef } from 'react';
import { range } from 'lodash';
import {
  ApplicationSourceEnum,
  RecruiterApplicationStateEnum,
  UserApplicationStateEnum,
  useRejectApplicationMutation,
} from '@src/graphql/generated';
import { FeaturedTalentCard, Placeholder, Skeleton } from '@src/containers/Feed/FeaturedTalentCard';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { useAppDispatch } from '@src/redux';
import { setModalContent } from '@src/redux/actions/modal';
import { InviteToInterview } from '@src/components/VerifyTalentCard/InviteToInterview';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { PENDING_APPLICANTS_FIRST_BATCH_SIZE, PENDING_APPLICANTS_NEXT_BATCH_SIZE } from '../constants/batchSizes';
import { QueryParams } from '../interfaces/queryParams.interface';
import { ActiveView } from '../enums/activeView.enum';
import { useGetOpeningApplicants } from '../hooks/useGetOpeningApplicants';
import { PendingInvitesProps } from './props';
import './styles.scss';
import { onRejectParams } from '../interfaces/onReject.interface';

export const PendingInvites: FC<PendingInvitesProps> = ({ pendingInvitesCount, opening }) => {
  const isMounted = useRef(false);

  const dispatch = useAppDispatch();

  const { updateQueryParams } = useQueryParams<QueryParams>();

  const {
    openingApplicants,
    openingApplicantsPagination,
    isOpeningApplicantsLoading,
    loadMoreOpeningApplicants,
    refetchOpeningApplicants,
  } = useGetOpeningApplicants({
    fetchPolicy: 'network-only',
    firstBatchSize: PENDING_APPLICANTS_FIRST_BATCH_SIZE,
    nextBatchSize: PENDING_APPLICANTS_NEXT_BATCH_SIZE,
    recruiterStates: [RecruiterApplicationStateEnum.PENDING, RecruiterApplicationStateEnum.ACCEPTED],
    sources: [ApplicationSourceEnum.USER_VERIFICATION],
    userStates: [UserApplicationStateEnum.PENDING, UserApplicationStateEnum.REJECTED],
  });

  const openVerifiedTalent = (userId: string): void => {
    dispatch(
      setModalContent({
        className: 'opening-pending-invites__modal-wrapper',
        config: {
          withoutPadding: true,
        },
        content: (
          <InviteToInterview
            areVerificationStagesHidden
            modalOptions={{
              shouldOnlyShowProfile: true,
            }}
            userId={userId}
          />
        ),
      }),
    );
  };

  const openOpeningApplicantChat = (applicationId: string): void => {
    updateQueryParams({
      applicationId: applicationId,
    });
  };

  const [rejectApplication] = useRejectApplicationMutation({
    onCompleted: async () => {
      await refetchOpeningApplicants();
    },
    onError: () => {},
  });

  const onReject = async ({ userMatchingProfileId, openingId }: onRejectParams) => {
    await rejectApplication({
      variables: {
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });

    trackEvent({
      context: MetricContext.OPENING_VIEW,
      interaction: MetricAction.REJECT_APPLICATION,
      payload: {
        'Opening Id': openingId,
        'User Matching Profile Id': userMatchingProfileId,
      },
    });
  };

  useLayoutEffect(() => {
    const checkScrollPositionAndLoadMore = () => {
      const {
        body: { scrollHeight },
      } = document;
      const { innerHeight, scrollY } = window;
      const distanceFromBottom = scrollHeight - innerHeight - scrollY;

      if (!isOpeningApplicantsLoading && openingApplicantsPagination?.hasMoreResults && distanceFromBottom < 100) {
        loadMoreOpeningApplicants();
      }
    };

    window.addEventListener('scroll', checkScrollPositionAndLoadMore);

    return () => {
      window.removeEventListener('scroll', checkScrollPositionAndLoadMore);

      loadMoreOpeningApplicants.cancel();
    };
  }, [isOpeningApplicantsLoading, openingApplicantsPagination?.hasMoreResults]);

  useEffect(() => {
    if (isMounted.current) {
      refetchOpeningApplicants();
    }

    isMounted.current = true;
  }, [pendingInvitesCount]);

  return (
    <div className="opening-pending-invites">
      <div className="opening-pending-invites__header">
        <span className="opening-pending-invites__header-title">Pending invites</span>

        <span className="opening-pending-invites__header-description">
          Available talent that you have invited to apply to your open position
        </span>
      </div>

      <div className="opening-pending-invites__cards">
        {openingApplicants.map(userData => (
          <FeaturedTalentCard
            key={userData.user.id}
            onCardClick={() => openVerifiedTalent(userData.user.id)}
            onFollowUp={() => openOpeningApplicantChat(userData.application.id)}
            onArchive={() => {
              const userMatchingProfileId = userData.application.userMatchingProfile.id;

              if (opening.id && userMatchingProfileId) {
                onReject({
                  openingId: opening.id,
                  userMatchingProfileId: userMatchingProfileId,
                });
              }
            }}
            userData={userData}
            wrapperClassName="opening-pending-invites__card"
          />
        ))}

        {!openingApplicantsPagination?.hasMoreResults && !isOpeningApplicantsLoading && (
          <Placeholder
            message="Invite candidates to start a conversation"
            onClick={() =>
              updateQueryParams({
                activeView: ActiveView.INVITE_CANDIDATES,
              })
            }
            wrapperClassName="opening-pending-invites__card"
          />
        )}

        {openingApplicants?.length < PENDING_APPLICANTS_FIRST_BATCH_SIZE &&
          !isOpeningApplicantsLoading &&
          range(1, PENDING_APPLICANTS_FIRST_BATCH_SIZE - openingApplicants.length).map(count => {
            return <Placeholder key={count} wrapperClassName="opening-pending-invites__card" />;
          })}

        {isOpeningApplicantsLoading && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </div>
    </div>
  );
};
